import * as React from "react";
import classnames from "classnames";
import { useState, useEffect } from "react";
import { ValidationFunc } from "./validation";

export type TValidateProp<P, T> = P & {
  validate: ValidationFunc<T>;
  onValidate?: (valid: boolean) => void;
};

export type TComponent<P> =
  | React.ComponentClass<P>
  | React.FunctionComponent<P>;

export function addValidation<P extends {}, T>(
  getValue: (p: P) => T,
  fieldId: string = ""
) {
  return (Component: TComponent<P>): TComponent<TValidateProp<P, T>> => {
    return (props: TValidateProp<P, T>) => {
      const { validate, onValidate, ...otherProps } = props;
      const origProps = otherProps as unknown as P & { id?: string };
      const [message = undefined] = validate(getValue(origProps));
      const [_valid, setValid] = useState(false);
      const id = `${fieldId || origProps.id}ErrorTooltip`;
      const alertId = `${fieldId || origProps.id}AlertMessage`;
      const [touched, setTouched] = useState(false);
      const [isFocused, setIsFocused] = useState(false);
      const valid = message === undefined;
      useEffect(() => {
        if (onValidate && _valid !== valid) {
          onValidate(valid);
          setValid(valid);
        }
      }, [onValidate, _valid, valid]);
      return (
        <div
          className={classnames({
            "validation-wrapper": true,
            valid: valid,
            invalid: !valid && touched,
            [`validation-wrapper-${origProps.id}`]: true,
          })}
        >
          <Component
            {...origProps}
            onFocus={() => {
              setTouched(true);
              setIsFocused(true);
            }}
            onBlur={() => setIsFocused(false)}
            ariaDescribedBy={!valid ? alertId : undefined}
            ariaInvalid={!valid && touched}
          />
          <div className={`validationLine ${isFocused ? "focused" : ""}`} />
          {!valid && touched && !isFocused && (
            <div id={id} className="validation-tooltip">
              <div id={alertId} role="alert" className="tooltip-message">
                {message}
              </div>
            </div>
          )}
        </div>
      );
    };
  };
}
